/* Proteam CRO: existing cart-total progress + mobile sticky Add to Cart.
   No independent /cart/add request; sticky button delegates to the NATIVE form.
   Main product form, variants, inventory rules, apps and checkout are unchanged. */
(function () {
  'use strict';
  const SELECTOR = '[data-pdh-shipping-progress]';
  const state = new WeakMap();

  function formatAmount(cents, currency, lang) {
    try {
      return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG-u-nu-latn' : 'en-EG', {
        style: 'currency', currency: currency || 'EGP', maximumFractionDigits: 0
      }).format(Math.max(0, cents) / 100);
    } catch (_) {
      return `${Math.round(Math.max(0, cents) / 100)} ${currency || 'EGP'}`;
    }
  }

  function findSubmit(s) {
    // Scope to THIS product section; never click a quick-add or related-product form.
    return s.section.querySelector('product-form form[data-type="add-to-cart-form"] .product-form__submit[name="add"]');
  }

  function shippingMessage(s, sticky) {
    if (s.shippingClass === 'paid') return s.lang === 'ar'
      ? (sticky ? 'تُحسب تكلفة الشحن عند إتمام الطلب' : 'هذه الباقة لا يشملها عرض الشحن المجاني. تُحدد تكلفة الشحن عند إتمام الطلب.')
      : (sticky ? 'Shipping calculated at checkout' : 'This bundle is excluded from free shipping. Delivery is calculated at checkout.');
    if (s.shippingClass === 'free') return s.lang === 'ar'
      ? (sticky ? 'الباقة تشمل الشحن المجاني' : 'الباقة تشمل شحن مجاني حسب منطقة التوصيل. قد تُضاف رسوم شحن لمنتجات أخرى.')
      : (sticky ? 'Free shipping for this bundle' : 'Free shipping for this bundle in eligible areas. Other items may have delivery charges.');

    const cartTotal = Math.max(0, s.cartTotal); // Eligible individual products only.
    const thresholdText = formatAmount(s.threshold, s.currency, s.lang);
    const remainingText = formatAmount(Math.max(0, s.threshold - cartTotal), s.currency, s.lang);
    if (s.lang === 'ar') {
      if (cartTotal >= s.threshold) return 'وصلت للحد المطلوب للشحن المجاني';
      if (cartTotal === 0) return sticky ? `شحن مجاني من ${thresholdText}` : `شحن مجاني للطلبات من ${thresholdText}`;
      return sticky ? `متبقي ${remainingText} للحصول على شحن مجاني` : `متبقي ${remainingText} للحصول على شحن مجاني`;
    }
    if (cartTotal >= s.threshold) return 'Free shipping on eligible items';
    if (cartTotal === 0) return sticky ? 'Free shipping on eligible items' : `Free shipping on eligible items from ${thresholdText}`;
    return sticky ? `${remainingText} until free shipping` : `${remainingText} until free shipping on eligible items`;
  }

  function paint(el) {
    const s = state.get(el);
    if (!s) return;
    el.hidden = false;
    const cartTotal = Math.max(0, s.cartTotal); // Never count excluded bundles.
    const percentage = Math.min(100, Math.max(0, cartTotal / s.threshold * 100));
    // Success state hides the completed progress track, but keeps native Add to Cart available.
    const goalMet = s.shippingClass === 'general' && cartTotal >= s.threshold;
    el.dataset.pdhGoalMet = String(goalMet);
    if (s.sticky) s.sticky.dataset.pdhGoalMet = String(goalMet);
    const totalText = formatAmount(cartTotal, s.currency, s.lang);
    const thresholdText = formatAmount(s.threshold, s.currency, s.lang);
    const counter = el.querySelector('[data-pdh-progress-counter]');
    const fill = el.querySelector('[data-pdh-progress-fill]');
    const track = el.querySelector('[data-pdh-progress-track]');
    const message = el.querySelector('[data-pdh-progress-message]');
    const warning = el.querySelector('[data-pdh-profile-warning]');
    el.dataset.cartTotal = String(cartTotal); // Live input to the existing governorate fee selector.
    if (counter) counter.textContent = `${totalText} / ${thresholdText}`;
    if (fill) fill.style.width = `${percentage}%`;
    if (track) track.setAttribute('aria-valuenow', String(Math.floor(percentage)));
    if (message) message.textContent = shippingMessage(s, false);
    if (warning) warning.hidden = !(s.shippingClass === 'general' && s.hasPaidBundleInCart);
    if (s.sticky) {
      const sc = s.sticky.querySelector('[data-pdh-sticky-counter]');
      const sf = s.sticky.querySelector('[data-pdh-sticky-fill]');
      const sm = s.sticky.querySelector('[data-pdh-sticky-message]');
      if (sc) sc.textContent = `${totalText} / ${thresholdText}`;
      if (sf) sf.style.width = `${percentage}%`;
      if (sm) sm.textContent = shippingMessage(s, true);
    }
    updateSticky(s);
  }

  function selectedVariantId(s) {
    // Shopify's native hidden variant input is the source of truth.
    const id = s.section.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]')?.value;
    return id ? String(id) : (s.variantId || '');
  }

  function currentVariantCartQuantity(s) {
    return s.cartQtyByVariant.get(selectedVariantId(s)) || 0;
  }

  function syncInCartIndicator(s) {
    const info = s.sticky?.querySelector('[data-pdh-sticky-in-cart]');
    if (!info) return;
    const count = currentVariantCartQuantity(s);
    info.hidden = count < 1;
    if (count > 0) {
      info.textContent = s.lang === 'ar' ? `${count} في السلة` : `Already in cart: ${count}`;
    }
  }

  function syncProductSummary(s) {
    if (!s.sticky) return;
    const summary = s.sticky.querySelector('[data-pdh-sticky-product]');
    if (!summary) return;
    // The quantity and actual selected variant always come from the native
    // Shopify product form/variant-change event. Never use a separate add request.
    const quantityInput = s.section.querySelector('.product-form__quantity .quantity__input');
    const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
    const quantityLabel = summary.querySelector('[data-pdh-sticky-quantity]');
    if (quantityLabel) quantityLabel.textContent = String(quantity);
    const priceLabel = summary.querySelector('[data-pdh-sticky-price]');
    const unitCents = Number(summary.dataset.pdhStickyUnitCents);
    if (priceLabel && Number.isFinite(unitCents) && unitCents >= 0) {
      priceLabel.textContent = formatAmount(unitCents * quantity, s.currency, s.lang);
    }
    syncInCartIndicator(s);
  }

  function updateSelectedSummary(s, html, variant) {
    if (!s.sticky || !html || !variant) return;
    // Shopify responds with the selected variant's freshly rendered section.
    // Reading the markup avoids inventing image/price URLs in JavaScript.
    const incoming = html.querySelector('[data-pdh-sticky-product]');
    const current = s.sticky.querySelector('[data-pdh-sticky-product]');
    if (!incoming || !current) return;
    const oldImage = current.querySelector('[data-pdh-sticky-image]');
    const newImage = incoming.querySelector('[data-pdh-sticky-image]');
    if (newImage) {
      if (oldImage) {
        oldImage.src = newImage.src;
        oldImage.alt = newImage.alt;
      } else {
        current.insertBefore(newImage.cloneNode(true), current.firstChild);
      }
    } else if (oldImage) {
      oldImage.remove();
    }
    const fields = ['[data-pdh-sticky-title]', '[data-pdh-sticky-variant]'];
    fields.forEach(selector => {
      const from = incoming.querySelector(selector);
      const to = current.querySelector(selector);
      if (from && to) to.textContent = from.textContent;
    });
    current.dataset.pdhStickyUnitCents = incoming.dataset.pdhStickyUnitCents;
    syncProductSummary(s);
  }

  function syncButton(s) {
    if (!s.sticky || !s.stickyButton) return;
    const original = findSubmit(s);
    const variantInput = s.section.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]');
    const disabled = !original || original.disabled || original.getAttribute('aria-disabled') === 'true' ||
      !variantInput || variantInput.disabled || !variantInput.value || !s.available;
    s.stickyButton.disabled = disabled;
    const label = original?.querySelector('span')?.textContent?.trim();
    if (original?.classList.contains('loading')) {
      s.stickyButton.textContent = s.lang === 'ar' ? 'جارٍ الإضافة...' : 'Adding...';
    } else if (label) {
      // Only change the standard add-to-cart text. Never override stock/error labels.
      const isAddLabel = /add to cart/i.test(label) || /أضف.*السلة/.test(label);
      if (!disabled && isAddLabel && currentVariantCartQuantity(s) > 0) {
        const qty = Math.max(1, Number.parseInt(s.section.querySelector('.product-form__quantity .quantity__input')?.value || '1', 10) || 1);
        s.stickyButton.textContent = s.lang === 'ar'
          ? (qty === 1 ? 'أضف قطعة أخرى' : `أضف ${qty} قطع أخرى`)
          : (qty === 1 ? 'Add another' : `Add ${qty} more`);
      } else {
        s.stickyButton.textContent = label;
      }
    }
  }

  function updateSticky(s) {
    if (!s.sticky) return;
    const original = findSubmit(s);
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const dialogOpen = !!document.querySelector('cart-drawer .drawer.active, cart-notification.active, dialog[open], #Details-menu-drawer-container[open]');
    const pageLocked = document.body.classList.contains('overflow-hidden');
    const focused = document.activeElement;
    // Fragrance swatches are radio inputs: focusing one must NOT hide the sticky CTA.
    // Only hide for actual text/number entry where a mobile keyboard may overlap it.
    const typingFocused = !!focused?.matches?.(
      'input[type="text"], input[type="search"], input[type="email"], input[type="tel"], ' +
      'input[type="url"], input[type="number"], input[type="password"], textarea, ' +
      'select, [contenteditable="true"]'
    );
    const buttonBox = original?.getBoundingClientRect();
    // Geometry is the source of truth: show ONLY when the original Add to Cart
    // is above the viewport, never while it is still below the first screen.
    // Do not latch a past-scroll flag, which caused the bar to reappear at top.
    const originalMeasured = !!buttonBox && buttonBox.width > 1 && buttonBox.height > 1;
    const passedOriginal = originalMeasured && buttonBox.bottom < -8;
    const show = Boolean(mobile && original && passedOriginal && !dialogOpen &&
      !pageLocked && !typingFocused);
    // Only touch layout when visibility changes. Scroll events should not
    // repeatedly remeasure and repaint the product summary on mobile.
    if (s.sticky.hidden !== !show) {
      s.sticky.hidden = !show;
      document.body.classList.toggle('pdh-sticky-atc-active', show);
      if (show) {
        document.body.style.setProperty('--pdh-sticky-atc-height', `${s.sticky.getBoundingClientRect().height}px`);
        syncProductSummary(s);
        syncButton(s);
      }
    }
  }

  // Shopify renders the actual shipping-tag eligibility in the cart drawer.
  // Never guess from names (especially bundle names) or from /cart.js,
  // which does not expose parent-bundle shipping tags.
  function syncShippingFromDrawer(el, emptyCart) {
    const s = state.get(el);
    if (!s) return false;
    const drawer = document.querySelector('#CartDrawerRoot');
    const bar = drawer?.querySelector('[data-pdh-cart-progress]');
    if (emptyCart === true || (!bar && drawer?.classList.contains('is-empty'))) {
      s.cartTotal = 0;
      s.hasPaidBundleInCart = false;
      s.hasFreeBundleInCart = false;
      paint(el);
      return true;
    }
    if (!bar) return false; // Never replace the true subtotal with a guessed 0.
    const eligible = Number(bar.dataset.eligibleCents);
    const threshold = Number(bar.dataset.thresholdCents);
    if (!Number.isFinite(eligible) || eligible < 0 || !Number.isFinite(threshold) || threshold < 100) return false;
    s.cartTotal = eligible;
    s.threshold = threshold;
    s.hasPaidBundleInCart = bar.dataset.cartPaid === 'true';
    s.hasFreeBundleInCart = bar.dataset.cartFree === 'true';
    paint(el);
    return true;
  }

  function applyCart(el, cart) {
    const s = state.get(el);
    if (!s || !cart || !Array.isArray(cart.items)) return;
    // Sync the variant quantity and native label from Shopify's actual cart.
      document.dispatchEvent(new CustomEvent('pdh:cart-data', { detail: { cart: cart } }));
      const quantities = new Map();
      for (const item of Array.isArray(cart.items) ? cart.items : []) {
        const id = String(item.variant_id ?? item.id ?? '');
        if (!id) continue;
        quantities.set(id, (quantities.get(id) || 0) + (Number(item.quantity) || 0));
      }
      s.cartQtyByVariant = quantities;
      s.currency = cart.currency || s.currency;
      syncProductSummary(s);
      syncButton(s);
      // The cart drawer is the single source of truth for shipping eligibility.
      // Cart remove events update it with fresh server-side Liquid.
      // If it isn't rendered yet, retain the previous verified value.
      syncShippingFromDrawer(el, cart.item_count === 0);
  }

  async function refreshCart(el) {
    const s = state.get(el);
    if (!s) return;
    const token = ++s.refreshToken;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root.replace(/\/$/, '') + '/cart.js', {
        credentials: 'same-origin', cache: 'no-store'
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (state.get(el) !== s || token !== s.refreshToken) return;
      applyCart(el, cart);
    } catch (_) { /* Don't show false free shipping on network failures. */ }
  }

  function cleanup(el) {
    const s = state.get(el);
    if (!s) return;
    s.unsubscribe.forEach(unsubscribe => unsubscribe());
    if (s.cartRefreshTimer) clearTimeout(s.cartRefreshTimer);
    if (s.stickyFrame) cancelAnimationFrame(s.stickyFrame);
    s.observer?.disconnect();
    s.drawerObserver?.disconnect();
    s.sticky?.remove();
    state.delete(el);
    document.body.classList.remove('pdh-sticky-atc-active');
    document.body.style.removeProperty('--pdh-sticky-atc-height');
  }

  function init(el) {
    if (state.has(el)) return;
    const section = el.closest('product-info');
    if (!section) return;
    const sticky = el.nextElementSibling?.matches('[data-pdh-sticky-atc]') ? el.nextElementSibling : null;
    const inCartStatus = sticky?.querySelector('[data-pdh-sticky-in-cart]');
    const initialVariantId = inCartStatus?.dataset.pdhInitialVariantId || '';
    const initialCount = Math.max(0, Number(inCartStatus?.dataset.pdhInitialCount) || 0);
    const s = {
      section,
      sticky,
      variantId: initialVariantId,
      cartQtyByVariant: new Map(initialVariantId ? [[initialVariantId, initialCount]] : []),
      stickyButton: sticky?.querySelector('[data-pdh-sticky-submit]'),
      threshold: Math.max(100, Number(el.dataset.threshold) || 100000),
      shippingClass: ['general', 'paid', 'free'].includes(el.dataset.shippingClass) ? el.dataset.shippingClass : 'general',
      paidHandles: new Set((el.dataset.paidBundleHandles || '').split('|').filter(Boolean)),
      freeHandles: new Set((el.dataset.freeBundleHandles || '').split('|').filter(Boolean)),
      hasPaidBundleInCart: el.dataset.hasPaidBundleCart === 'true',
      hasFreeBundleInCart: el.dataset.hasFreeBundleCart === 'true',
      cartTotal: Math.max(0, Number(el.dataset.cartTotal) || 0),
      available: el.dataset.variantAvailable === 'true',
      currency: el.dataset.currency || 'EGP',
      lang: el.dataset.lang === 'ar' ? 'ar' : 'en',
      refreshToken: 0,
      cartRefreshTimer: null,
      stickyFrame: 0,
      unsubscribe: []
    };
    state.set(el, s);
    const drawer = document.querySelector('#CartDrawerRoot');
    if (drawer) {
      // Native Dawn re-renders drawer HTML after add, remove, and +/- changes.
      // Observe only those replacements; no polling or extra cart requests.
      s.drawerObserver = new MutationObserver(() => { syncShippingFromDrawer(el); });
      const inner = drawer.querySelector('#CartDrawer');
      if (inner) s.drawerObserver.observe(inner, { childList: true, subtree: true });
    }
    if (sticky) {
      sticky.setAttribute('dir', s.lang === 'ar' ? 'rtl' : 'ltr');
      document.body.appendChild(sticky); // Avoid fixed positioning being clipped by the product grid.
      const onStickyClick = function () {
        const native = findSubmit(s);
        syncButton(s);
        if (native && !s.stickyButton.disabled) {
          // Immediate feedback, while the native product form still owns the
          // actual add request, error handling, and cart drawer response.
          s.stickyButton.disabled = true;
          s.stickyButton.textContent = s.lang === 'ar' ? 'جارٍ الإضافة...' : 'Adding...';
          native.click(); // Native Shopify AJAX handling: never submit twice.
          window.requestAnimationFrame(() => { syncButton(s); updateSticky(s); });
        }
      };
      s.stickyButton?.addEventListener('click', onStickyClick);
      s.unsubscribe.push(() => s.stickyButton?.removeEventListener('click', onStickyClick));
    }
    const original = findSubmit(s);
    if (original && sticky) {
      s.observer = new MutationObserver(() => { syncButton(s); updateSticky(s); });
      s.observer.observe(original, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], subtree: false });
    }
    // At most one visibility/layout check per animation frame while scrolling.
    const onScroll = () => {
      if (s.stickyFrame) return;
      s.stickyFrame = window.requestAnimationFrame(() => {
        s.stickyFrame = 0;
        updateSticky(s);
      });
    };
    const onFocus = () => updateSticky(s);
    const onVariantInput = () => { syncProductSummary(s); syncButton(s); updateSticky(s); };
    const onToggle = () => window.requestAnimationFrame(() => updateSticky(s));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onFocus);
    document.addEventListener('toggle', onToggle, true);
    section.addEventListener('change', onVariantInput);
    section.addEventListener('input', onVariantInput);
    s.unsubscribe.push(() => window.removeEventListener('scroll', onScroll));
    s.unsubscribe.push(() => window.removeEventListener('resize', onScroll));
    s.unsubscribe.push(() => document.removeEventListener('focusin', onFocus));
    s.unsubscribe.push(() => document.removeEventListener('focusout', onFocus));
    s.unsubscribe.push(() => document.removeEventListener('toggle', onToggle, true));
    s.unsubscribe.push(() => section.removeEventListener('change', onVariantInput));
    s.unsubscribe.push(() => section.removeEventListener('input', onVariantInput));

    const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    if (typeof window.subscribe === 'function' && events) {
      if (events.variantChange) {
        const unsub = window.subscribe(events.variantChange, event => {
          if (event?.data?.sectionId !== s.section.dataset.section) return;
          s.available = Boolean(event?.data?.variant?.available);
          s.variantId = event?.data?.variant?.id ? String(event.data.variant.id) : '';
          updateSelectedSummary(s, event?.data?.html, event?.data?.variant);
          paint(el);
          syncButton(s);
        });
        if (typeof unsub === 'function') s.unsubscribe.push(unsub);
      }
      if (events.cartUpdate) {
        // Shopify already updates the drawer from the /cart/add response.
        // Defer and coalesce our EXTRA /cart.js progress-bar refresh to avoid
        // competing with the native cart drawer during that critical paint.
        const unsub = window.subscribe(events.cartUpdate, event => {
          if (s.cartRefreshTimer) clearTimeout(s.cartRefreshTimer);
          // A quantity edit/removal already carries Shopify's complete cart.
          // Reuse it immediately and invalidate older in-flight cart requests.
          if (Array.isArray(event?.cartData?.items)) {
            ++s.refreshToken;
            s.cartRefreshTimer = null;
            applyCart(el, event.cartData);
            return;
          }
          // A product add contains only the added item, not the full cart.
          s.cartRefreshTimer = window.setTimeout(() => {
            s.cartRefreshTimer = null;
            refreshCart(el);
          }, 250);
        });
        if (typeof unsub === 'function') s.unsubscribe.push(unsub);
      }
    }
    syncProductSummary(s);
    syncButton(s);
    paint(el);
    refreshCart(el);
  }

  function scan(root) {
    if (root?.matches?.(SELECTOR)) init(root);
    (root || document).querySelectorAll(SELECTOR).forEach(init);
  }
  function start() { scan(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(SELECTOR).forEach(cleanup);
  });
  document.addEventListener('product-info:loaded', event => { if (event.target?.matches('product-info')) scan(event.target); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) document.querySelectorAll(SELECTOR).forEach(refreshCart); });
  // Initial page load is already fetched by init(); refresh only on BFCache restore.
  window.addEventListener('pageshow', event => { if (event.persisted) document.querySelectorAll(SELECTOR).forEach(refreshCart); });
})();
